import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bold, Italic, Strikethrough, Quote, Heading1, Heading2, Heading3, Highlighter, Link2,
  MoreHorizontal, Share2, FileDown, History, Trash2, X, CircleCheck, ChevronRight,
  Rows3, Columns3, Trash, PanelRightClose, PanelRightOpen, PanelLeftOpen,
  GripVertical, Plus, ChevronDown, ExternalLink, Unlink, Check, Type, List, ListOrdered, ListChecks, Lightbulb,
  Palette, Copy,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { DragHandle } from "@tiptap/extension-drag-handle-react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Highlight from "@tiptap/extension-highlight";
import { TableKit } from "@tiptap/extension-table";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import { Details, DetailsContent, DetailsSummary } from "@tiptap/extension-details";
import { TextStyle, Color, BackgroundColor } from "@tiptap/extension-text-style";
import { api, formatApiError } from "@/lib/api";
import { SakKobling } from "./SakKobling";
import { SLASH_VALG, filtrerSlash, SlashMeny } from "./SlashMeny";
import { PipelineNode, SakerNode, EntitetNode, BeslutningNode } from "./blokker/blokkNoder";
import { MentionNode } from "./blokker/MentionNode";
import { blokkSok, hentMedlemmer } from "./blokker/blokkData";
import DokToc from "./DokToc";
import { DokIkonCover } from "./DokIkonCover";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const BOBLE_OPTIONS = { placement: "top", offset: 8 };
const bobleShouldShow = ({ editor: e, state }) => e.isEditable && !state.selection.empty && !e.isActive("image") && !e.isActive("codeBlock");

const LIVE_BLOKKER = ["pipelineBlokk", "sakerBlokk", "entitetBlokk", "beslutningBlokk"];

const OMGJOR_VALG = [
  { id: "tekst", label: "Tekst", Ico: Type, aktiv: (e) => e.isActive("paragraph") && !e.isActive("bulletList") && !e.isActive("orderedList") && !e.isActive("taskList") && !e.isActive("blockquote") && !e.isActive("callout"), run: (e) => e.chain().focus().clearNodes().run() },
  { id: "h1", label: "Overskrift 1", Ico: Heading1, aktiv: (e) => e.isActive("heading", { level: 1 }), run: (e) => e.chain().focus().clearNodes().setNode("heading", { level: 1 }).run() },
  { id: "h2", label: "Overskrift 2", Ico: Heading2, aktiv: (e) => e.isActive("heading", { level: 2 }), run: (e) => e.chain().focus().clearNodes().setNode("heading", { level: 2 }).run() },
  { id: "h3", label: "Overskrift 3", Ico: Heading3, aktiv: (e) => e.isActive("heading", { level: 3 }), run: (e) => e.chain().focus().clearNodes().setNode("heading", { level: 3 }).run() },
  { id: "punkt", label: "Punktliste", Ico: List, aktiv: (e) => e.isActive("bulletList"), run: (e) => e.chain().focus().clearNodes().toggleBulletList().run() },
  { id: "nummer", label: "Nummerert liste", Ico: ListOrdered, aktiv: (e) => e.isActive("orderedList"), run: (e) => e.chain().focus().clearNodes().toggleOrderedList().run() },
  { id: "sjekk", label: "Sjekkliste", Ico: ListChecks, aktiv: (e) => e.isActive("taskList"), run: (e) => e.chain().focus().clearNodes().toggleTaskList().run() },
  { id: "sitat", label: "Sitat", Ico: Quote, aktiv: (e) => e.isActive("blockquote"), run: (e) => e.chain().focus().clearNodes().toggleBlockquote().run() },
  { id: "callout", label: "Callout", Ico: Lightbulb, aktiv: (e) => e.isActive("callout"), run: (e) => e.chain().focus().clearNodes().toggleCallout().run() },
];

const TEKST_TYPER = ["paragraph", "heading", "bulletList", "orderedList", "taskList", "blockquote", "callout"];

const TEKST_FARGER = [
  { id: "standard", label: "Standard", farge: null },
  { id: "graa", label: "Grå", farge: "#787774" },
  { id: "brun", label: "Brun", farge: "#9F6B53" },
  { id: "oransje", label: "Oransje", farge: "#D9730D" },
  { id: "gul", label: "Gul", farge: "#CB912F" },
  { id: "gronn", label: "Grønn", farge: "#448361" },
  { id: "blaa", label: "Blå", farge: "#337EA9" },
  { id: "lilla", label: "Lilla", farge: "#9065B0" },
  { id: "rosa", label: "Rosa", farge: "#C14C8A" },
  { id: "rod", label: "Rød", farge: "#D44C47" },
];
const BAKGRUNN_FARGER = [
  { id: "ingen", label: "Ingen", farge: null },
  { id: "graa", label: "Grå", farge: "#F1F1EF" },
  { id: "brun", label: "Brun", farge: "#F3EEEE" },
  { id: "oransje", label: "Oransje", farge: "#FAEBDD" },
  { id: "gul", label: "Gul", farge: "#FBF3DB" },
  { id: "gronn", label: "Grønn", farge: "#EDF3EC" },
  { id: "blaa", label: "Blå", farge: "#E7F3F8" },
  { id: "lilla", label: "Lilla", farge: "#F6F3F9" },
  { id: "rosa", label: "Rosa", farge: "#FAF1F5" },
  { id: "rod", label: "Rød", farge: "#FDEBEC" },
];

const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "paragraph+",
  defining: true,
  addAttributes: () => ({
    variant: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-variant"),
      renderHTML: (a) => (a.variant ? { "data-variant": a.variant } : {}),
    },
    tone: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-tone"),
      renderHTML: (a) => (a.tone ? { "data-tone": a.tone } : {}),
    },
    ikon: {
      default: null,
      parseHTML: (el) => el.getAttribute("data-ikon"),
      renderHTML: (a) => (a.ikon ? { "data-ikon": a.ikon } : {}),
    },
  }),
  parseHTML: () => [{ tag: "div.dok-callout" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes({ class: "dok-callout" }, HTMLAttributes), 0],
  addCommands() {
    return { toggleCallout: () => ({ commands }) => commands.toggleWrap(this.name) };
  },
});

const KortGrid = Node.create({
  name: "kortGrid",
  group: "block",
  content: "callout+",
  defining: true,
  parseHTML: () => [{ tag: "div.dok-kortgrid" }],
  renderHTML: () => ["div", { class: "dok-kortgrid" }, 0],
});

const BKnapp = ({ onClick, aktiv, title, children, testid }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`dok-bknapp ${aktiv ? "aktiv" : ""}`} title={title} data-testid={testid}>
    {children}
  </button>
);

export default function DokEditor({ doc, linkedTasks, parentTitle, aktor, onLagret, onSlett, treSkjult, onToggleTre }) {
  const [status, setStatus] = useState("");
  const [meny, setMeny] = useState(false);
  const [delModal, setDelModal] = useState(false);

  useEffect(() => {
    if (!delModal) return undefined;
    const esc = (e) => { if (e.key === "Escape") setDelModal(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [delModal]);
  const [versjoner, setVersjoner] = useState(null);
  const [item, setItem] = useState(doc);
  const [saker, setSaker] = useState(linkedTasks);
  const [ord, setOrd] = useState(0);
  const [overskrifter, setOverskrifter] = useState([]);
  const [slash, setSlash] = useState(null);
  const [slashIdx, setSlashIdx] = useState(0);
  const [mention, setMention] = useState(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mentionTreff, setMentionTreff] = useState([]);
  const [aaMeny, setAaMeny] = useState(false);
  const mentionRef = useRef({});
  const [lenkePop, setLenkePop] = useState(null);
  const [omgjor, setOmgjor] = useState(false);
  const [blokkMeny, setBlokkMeny] = useState(null);
  const [fargeMeny, setFargeMeny] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [progress, setProgress] = useState(0);
  const dragInfo = useRef({ node: null, pos: 0 });
  const lenkeRef = useRef(() => {});
  const [tocSkjult, setTocSkjult] = useState(() => localStorage.getItem("nas_dok_toc") === "1");
  const toggleToc = () => setTocSkjult((v) => {
    localStorage.setItem("nas_dok_toc", v ? "0" : "1");
    return !v;
  });
  const filRef = useRef(null);
  const lagreT = useRef(null);
  const tittelT = useRef(null);
  const tocT = useRef(null);
  const slashRef = useRef({});
  const bildeRef = useRef(() => {});
  const pulsRef = useRef(() => {});

  useEffect(() => { setItem(doc); setSaker(linkedTasks); setVersjoner(null); }, [doc, linkedTasks]);

  const patch = useCallback(async (felter, stille = false) => {
    try {
      const { data } = await api.patch(`/admin/docs/${doc.id}`, { ...felter, actor: aktor });
      setItem(data.item);
      if (felter.linkedTaskIds !== undefined) {
        const { data: d2 } = await api.get(`/admin/docs/${doc.id}`);
        setSaker(d2.linkedTasks || []);
      }
      if (!stille) setStatus(`Lagret ${new Date().toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`);
      onLagret?.(data.item);
      if (felter.content !== undefined) pulsRef.current?.();
    } catch (e) { feil(e); }
  }, [doc.id, aktor, onLagret]);

  const oppdaterMeta = useCallback((e) => {
    setOrd(e.state.doc.textContent.trim().split(/\s+/).filter(Boolean).length);
    clearTimeout(tocT.current);
    tocT.current = setTimeout(() => {
      const hs = [];
      e.state.doc.descendants((node, pos) => {
        if (node.type.name === "details") {
          hs.push({ tekst: node.firstChild?.textContent || "…", level: 2, pos: pos + 1 });
          return false;
        }
        if (node.type.name === "pipelineBlokk") {
          hs.push({ tekst: node.attrs.spec?.tittel || "Pipeline", level: 2, pos });
          return false;
        }
        if (node.type.name === "sakerBlokk" && node.attrs.spec?.modus === "uke") {
          hs.push({ tekst: node.attrs.spec?.tittel || "Denne uken", level: 2, pos });
          return false;
        }
        if (node.type.name === "heading") hs.push({ tekst: node.textContent, level: node.attrs.level, pos });
        return true;
      });
      setOverskrifter(hs);
    }, 400);
  }, []);

  const sjekkSlash = useCallback((e) => {
    const { state, view } = e;
    const { $from, empty } = state.selection;
    if (!empty || $from.parent.type.name !== "paragraph") { setSlash(null); return; }
    const tekst = $from.parent.textBetween(0, $from.parentOffset, "\n", "\n");
    const m = tekst.match(/(?:^|\s)\/([a-zA-Z0-9æøåÆØÅ]*)$/);
    if (!m) { setSlash(null); return; }
    const koord = view.coordsAtPos($from.pos);
    setSlash({ x: Math.min(koord.left, window.innerWidth - 290), y: koord.bottom + 6, topY: koord.top, query: m[1].toLowerCase(), fra: $from.pos - m[1].length - 1 });
    setSlashIdx(0);
  }, []);

  const sjekkMention = useCallback((e) => {
    const { state, view } = e;
    const { $from, empty } = state.selection;
    if (!empty || !$from.parent.isTextblock) { setMention(null); return; }
    const tekst = $from.parent.textBetween(0, $from.parentOffset, "\n", "\n");
    const m = tekst.match(/(?:^|\s)@([^@\n]{0,40})$/);
    if (!m || /\s\s$/.test(m[1])) { setMention(null); return; }
    const koord = view.coordsAtPos($from.pos);
    setMention({ x: Math.min(koord.left, window.innerWidth - 330), y: koord.bottom + 6, topY: koord.top, query: m[1], fra: $from.pos - m[1].length - 1 });
    setMentionIdx(0);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: { openOnClick: false } }),
      Callout,
      KortGrid,
      Image,
      Highlight,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      BackgroundColor,
      Details.configure({ persist: true, HTMLAttributes: { class: "dok-toggle" } }),
      DetailsSummary,
      DetailsContent,
      TableKit.configure({ table: { resizable: false } }),
      PipelineNode,
      SakerNode,
      EntitetNode,
      BeslutningNode,
      MentionNode,
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === "heading") return "Overskrift";
          if (node.type.name === "detailsSummary") return "Toggle-tittel";
          return "Skriv, eller trykk «/» for blokker …";
        },
      }),
    ],
    content: doc.content || "",
    editable: true,
    shouldRerenderOnTransaction: true,
    editorProps: {
      handleKeyDown: (view, event) => {
        if (mentionRef.current.taster?.(event)) return true;
        if (slashRef.current.taster?.(event)) return true;
        if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === "k") {
          event.preventDefault();
          lenkeRef.current("meny");
          return true;
        }
        if ((event.key === "Backspace" || event.key === "Delete") && !view.state.selection.empty) {
          const { from, to } = view.state.selection;
          let harBlokk = false;
          view.state.doc.nodesBetween(from, to, (n) => {
            if (LIVE_BLOKKER.includes(n.type.name)) { harBlokk = true; return false; }
            return true;
          });
          // eslint-disable-next-line no-alert
          if (harBlokk && !window.confirm("Markeringen inneholder levende blokker koblet til systemet. Vil du slette dem likevel?")) return true;
        }
        return false;
      },
      handleClickOn: (view, pos, node) => LIVE_BLOKKER.includes(node.type.name),
      handleClick: (view, pos) => {
        const lm = view.state.doc.resolve(pos).marks().find((m) => m.type.name === "link");
        if (!lm) return false;
        lenkeRef.current("klikk", pos, lm.attrs.href);
        return true;
      },
      handlePaste: (view, event) => {
        const fil = Array.from(event.clipboardData?.files || []).find((f) => f.type.startsWith("image/"));
        if (fil) { bildeRef.current(fil); return true; }
        return false;
      },
      handleDrop: (view, event, _slice, flyttet) => {
        if (flyttet) return false;
        const fil = Array.from(event.dataTransfer?.files || []).find((f) => f.type.startsWith("image/"));
        if (fil) { event.preventDefault(); bildeRef.current(fil); return true; }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      setStatus("Lagrer …");
      clearTimeout(lagreT.current);
      lagreT.current = setTimeout(() => patch({ content: e.getHTML() }), 1200);
      oppdaterMeta(e);
      sjekkSlash(e);
      sjekkMention(e);
    },
    onSelectionUpdate: ({ editor: e }) => { sjekkSlash(e); sjekkMention(e); setOmgjor(false); setFargeMeny(false); },
    onCreate: ({ editor: e }) => oppdaterMeta(e),
  }, [doc.id]);

  useEffect(() => () => { clearTimeout(lagreT.current); clearTimeout(tocT.current); }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setStuck(window.scrollY > 150);
        const el = document.documentElement;
        const maks = el.scrollHeight - el.clientHeight;
        setProgress(maks > 0 ? Math.min(100, (window.scrollY / maks) * 100) : 0);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const kolonneRef = useRef(null);
  const [toppPos, setToppPos] = useState(null);
  useEffect(() => {
    const maal = () => {
      const el = kolonneRef.current;
      if (!el) return;
      const cs = getComputedStyle(el);
      const plass = el.getBoundingClientRect().width + (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
      el.style.setProperty("--dok-plass", `${Math.max(0, Math.floor(plass) - 16)}px`);
      if (window.innerWidth < 1024) { setToppPos(null); return; }
      const r = el.getBoundingClientRect();
      setToppPos({ left: Math.round(r.left), width: Math.round(r.width) });
    };
    maal();
    const t = setTimeout(maal, 350);
    window.addEventListener("resize", maal);
    const ro = kolonneRef.current ? new ResizeObserver(maal) : null;
    if (ro) ro.observe(kolonneRef.current);
    return () => { clearTimeout(t); window.removeEventListener("resize", maal); ro?.disconnect(); };
  }, [treSkjult, tocSkjult]);

  useEffect(() => { if (editor) editor.dokId = doc.id; }, [editor, doc.id]);

  const [puls, setPuls] = useState(null);
  const [bylineBilde, setBylineBilde] = useState("");
  useEffect(() => {
    const navn = (item.updatedBy || "").toLowerCase();
    hentMedlemmer()
      .then((ms) => setBylineBilde(ms.find((m) => (m.name || "").toLowerCase() === navn)?.bilde || ""))
      .catch(() => setBylineBilde(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.updatedBy]);
  const hentPuls = useCallback(() => {
    api.get(`/admin/docs/${doc.id}/puls`).then(({ data }) => setPuls(data)).catch(() => {});
  }, [doc.id]);
  useEffect(() => { hentPuls(); }, [hentPuls]);
  useEffect(() => { pulsRef.current = hentPuls; }, [hentPuls]);

  const lastOppBilde = useCallback(async (fil) => {
    if (!fil || !editor) return;
    const fd = new FormData();
    fd.append("fil", fil);
    try {
      setStatus("Laster opp bilde …");
      const { data } = await api.post(`/admin/docs/${doc.id}/media`, fd);
      editor.chain().focus().setImage({ src: data.url }).run();
      setStatus("");
    } catch (e) { feil(e); setStatus(""); }
  }, [editor, doc.id]);
  useEffect(() => { bildeRef.current = lastOppBilde; }, [lastOppBilde]);

  const slashValg = filtrerSlash(slash?.query);
  const kjorSlash = useCallback((valg) => {
    if (!editor || !slash) return;
    editor.chain().focus().deleteRange({ from: slash.fra, to: editor.state.selection.from }).run();
    setSlash(null);
    if (valg.id === "bilde") filRef.current?.click();
    else valg.run(editor);
  }, [editor, slash]);

  useEffect(() => {
    slashRef.current.taster = (event) => {
      if (!slash) return false;
      if (event.key === "ArrowDown") { setSlashIdx((i) => (i + 1) % Math.max(slashValg.length, 1)); return true; }
      if (event.key === "ArrowUp") { setSlashIdx((i) => (i - 1 + Math.max(slashValg.length, 1)) % Math.max(slashValg.length, 1)); return true; }
      if (event.key === "Enter") { if (slashValg[slashIdx]) kjorSlash(slashValg[slashIdx]); return true; }
      if (event.key === "Escape") { setSlash(null); return true; }
      return false;
    };
  }, [slash, slashValg, slashIdx, kjorSlash]);

  useEffect(() => {
    if (!aaMeny) return undefined;
    const esc = (e) => { if (e.key === "Escape") setAaMeny(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [aaMeny]);

  const settInnMention = useCallback((valg) => {
    if (!editor || !mention) return;
    editor.chain().focus()
      .deleteRange({ from: mention.fra, to: editor.state.selection.from })
      .insertContent([{ type: "mention", attrs: { kilde: valg.kilde, eid: valg.id, label: valg.navn, bilde: valg.bilde || "" } }, { type: "text", text: " " }])
      .run();
    setMention(null);
  }, [editor, mention]);

  useEffect(() => {
    if (!mention) { setMentionTreff([]); return undefined; }
    const t = setTimeout(() => blokkSok(mention.query, "speakers,partners,tasks,users")
      .then((r) => setMentionTreff(r.slice(0, 8)))
      .catch(() => setMentionTreff([])), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mention?.query]);

  useEffect(() => {
    mentionRef.current.taster = (event) => {
      if (!mention) return false;
      if (event.key === "ArrowDown") { setMentionIdx((i) => (i + 1) % Math.max(mentionTreff.length, 1)); return true; }
      if (event.key === "ArrowUp") { setMentionIdx((i) => (i - 1 + Math.max(mentionTreff.length, 1)) % Math.max(mentionTreff.length, 1)); return true; }
      if (event.key === "Enter") { if (mentionTreff[mentionIdx]) settInnMention(mentionTreff[mentionIdx]); return true; }
      if (event.key === "Escape") { setMention(null); return true; }
      return false;
    };
  }, [mention, mentionTreff, mentionIdx, settInnMention]);

  const apneLenke = useCallback((modus, pos, href) => {
    if (!editor) return;
    if (modus === "klikk") editor.chain().focus().setTextSelection(pos).extendMarkRange("link").run();
    let koord;
    try { koord = editor.view.coordsAtPos(editor.state.selection.from); } catch { return; }
    setLenkePop({
      x: Math.max(12, Math.min(koord.left, window.innerWidth - 340)),
      y: koord.bottom + 8,
      href: href ?? (editor.getAttributes("link").href || ""),
    });
  }, [editor]);
  useEffect(() => { lenkeRef.current = apneLenke; }, [apneLenke]);

  const lagreLenke = (url) => {
    if (!editor) return;
    const u = (url || "").trim();
    if (!u) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: /^(https?:\/\/|mailto:|\/)/i.test(u) ? u : `https://${u}` }).run();
    setLenkePop(null);
  };

  const settInnNy = () => {
    const { node, pos } = dragInfo.current;
    if (!node || !editor) return;
    const til = pos + node.nodeSize;
    editor.chain().focus().insertContentAt(til, { type: "paragraph" }).setTextSelection(til + 1).insertContent("/").run();
  };

  const dupliserBlokk = () => {
    const { node, pos } = blokkMeny;
    const json = node.toJSON();
    if (json.attrs?.spec?.bid) json.attrs = { ...json.attrs, spec: { ...json.attrs.spec, bid: Math.random().toString(36).slice(2, 10) } };
    editor.chain().focus().insertContentAt(pos + node.nodeSize, json).run();
    setBlokkMeny(null);
  };

  const slettBlokk = () => {
    const { node, pos } = blokkMeny;
    // eslint-disable-next-line no-alert
    if (LIVE_BLOKKER.includes(node.type.name) && !window.confirm("Dette er en levende blokk koblet til systemet. Slette den?")) { setBlokkMeny(null); return; }
    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
    setBlokkMeny(null);
  };

  const omgjorBlokk = (valg) => {
    editor.chain().focus().setTextSelection(blokkMeny.pos + 1).run();
    valg.run(editor);
    setBlokkMeny(null);
  };

  const hentVersjoner = async () => {
    setMeny(false);
    try {
      const { data } = await api.get(`/admin/docs/${doc.id}/versions`);
      setVersjoner(data.versions || []);
    } catch (e) { feil(e); }
  };

  const gjenopprett = async (vid) => {
    try {
      const { data } = await api.post(`/admin/docs/${doc.id}/versions/${vid}/restore`);
      setItem(data.item);
      editor?.commands.setContent(data.item.content || "");
      setVersjoner(null);
      toast.success("Versjonen er gjenopprettet");
    } catch (e) { feil(e); }
  };

  const delDok = async () => {
    setMeny(false);
    try {
      const { data } = await api.post(`/admin/docs/${doc.id}/share`);
      setItem(data.item);
      setDelModal(true);
    } catch (e) { feil(e); }
  };

  const settDataMode = async (m) => {
    try {
      const { data } = await api.patch(`/admin/docs/${doc.id}/share`, { dataMode: m });
      setItem(data.item);
    } catch (e) { feil(e); }
  };

  const oppdaterSnapshot = async () => {
    try {
      const { data } = await api.post(`/admin/docs/${doc.id}/share`);
      setItem(data.item);
      toast.success("Snapshot oppdatert med ferske data");
    } catch (e) { feil(e); }
  };

  const lastNedPdf = async () => {
    setMeny(false);
    try {
      toast.info("Genererer PDF …");
      const res = await api.get(`/admin/docs/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.title || "dokument"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { feil(e); }
  };

  if (!editor) return null;

  const delUrl = item.shareToken ? `${window.location.origin}/dok/s/${item.shareToken}` : "";
  const lesetid = Math.max(1, Math.round(ord / 200));
  const iTabell = editor.isActive("table");
  const omgjorAktiv = OMGJOR_VALG.find((v) => v.aktiv(editor)) || OMGJOR_VALG[0];
  const OmgjorIco = omgjorAktiv.Ico;

  return (
    <div className="flex gap-10 items-start" data-testid="dok-editor">
      <div className={`flex-1 min-w-0 ${item.pageWide ? "max-w-[1150px] dok-fullbredde" : "max-w-[760px]"} mx-auto dok-redig`} ref={kolonneRef}>
        <div className={`dok-topprad ${stuck ? "stuck" : ""} ${toppPos ? "i-nav" : ""}`}
          style={toppPos ? { left: toppPos.left, width: toppPos.width } : undefined}
          data-testid="dok-topprad">
        <div className="flex items-center gap-1.5 text-[11px] text-neutral-400" data-testid="dok-sti">
          {treSkjult && (
            <button type="button" onClick={onToggleTre} className="dok-hknapp ikon mr-1.5 hidden md:inline-flex" title="Vis dokumentliste" data-testid="dok-tre-vis">
              <PanelLeftOpen size={13} />
            </button>
          )}
          <span>Dokumenter</span>
          {parentTitle && (<><ChevronRight size={10} /><span className="truncate max-w-[140px]">{parentTitle}</span></>)}
          <ChevronRight size={10} />
          <span className="text-neutral-600 truncate max-w-[180px]">{item.title || "Uten tittel"}</span>
          <span className="flex-1" />
          <span className="text-[11px] text-neutral-300 whitespace-nowrap hidden lg:block mr-1" data-testid="dok-lagre-status">{status}</span>
          <button type="button" onClick={delDok} className="dok-hknapp" data-testid="dok-del-btn">
            <Share2 size={12} />
            <span className="hidden sm:inline">Del</span>
            {item.shareActive && <span className="dok-delt-dot" />}
          </button>
          <button type="button" onClick={lastNedPdf} className="dok-hknapp" data-testid="dok-pdf-btn">
            <FileDown size={12} /><span className="hidden sm:inline">PDF</span>
          </button>
          <div className="relative">
            <button type="button" onClick={() => setAaMeny((v) => !v)} className="dok-hknapp ikon dok-aa-btn" title="Sideinnstillinger" data-testid="dok-aa-btn">Aa</button>
            {aaMeny && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setAaMeny(false)} />
                <div className="dok-aa-meny absolute right-0 top-full mt-1.5 z-40 bg-white border border-[#E4E4E1] rounded-xl shadow-xl" data-testid="dok-aa-meny">
                  <p className="dok-aa-label">Font</p>
                  <div className="dok-aa-seg">
                    <button type="button" className={item.pageFont !== "sans" ? "aktiv" : ""} onClick={() => patch({ pageFont: "serif" }, true)} data-testid="dok-aa-serif"><span className="ag serif">Ag</span>Serif</button>
                    <button type="button" className={item.pageFont === "sans" ? "aktiv" : ""} onClick={() => patch({ pageFont: "sans" }, true)} data-testid="dok-aa-sans"><span className="ag sans">Ag</span>Sans</button>
                  </div>
                  <p className="dok-aa-label">Tekststørrelse</p>
                  <div className="dok-aa-seg">
                    <button type="button" className={!item.pageSmall ? "aktiv" : ""} onClick={() => patch({ pageSmall: false }, true)} data-testid="dok-aa-normal">Normal</button>
                    <button type="button" className={item.pageSmall ? "aktiv" : ""} onClick={() => patch({ pageSmall: true }, true)} data-testid="dok-aa-liten">Liten</button>
                  </div>
                  <p className="dok-aa-label">Bredde</p>
                  <div className="dok-aa-seg">
                    <button type="button" className={!item.pageWide ? "aktiv" : ""} onClick={() => patch({ pageWide: false }, true)} data-testid="dok-aa-standard">Standard</button>
                    <button type="button" className={item.pageWide ? "aktiv" : ""} onClick={() => patch({ pageWide: true }, true)} data-testid="dok-aa-full">Full bredde</button>
                  </div>
                </div>
              </>
            )}
          </div>
          <button type="button" onClick={toggleToc}
            className="dok-hknapp ikon hidden xl:inline-flex"
            title={tocSkjult ? "Vis innholdsfortegnelse" : "Skjul innholdsfortegnelse — dokumentet sentreres"}
            data-testid="dok-toc-toggle">
            {tocSkjult ? <PanelRightOpen size={13} /> : <PanelRightClose size={13} />}
          </button>
          <div className="relative">
            <button type="button" onClick={() => setMeny((v) => !v)} className="dok-hknapp ikon" data-testid="dok-meny-btn">
              <MoreHorizontal size={14} />
            </button>
            {meny && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMeny(false)} />
                <div className="sak-modal absolute right-0 top-full mt-1.5 z-40 w-[210px] bg-white border border-[#E4E4E1] rounded-xl shadow-xl p-1.5" data-testid="dok-meny">
                  <button type="button" onClick={hentVersjoner} className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12.5px] text-left text-neutral-700 hover:bg-neutral-50" data-testid="dok-versjoner-btn">
                    <History size={13} /> Versjonshistorikk
                  </button>
                  <div className="my-1 border-t border-neutral-100" />
                  <button type="button" onClick={() => { setMeny(false); onSlett(); }} className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12.5px] text-left text-red-600 hover:bg-red-50" data-testid="dok-slett-btn">
                    <Trash2 size={13} /> Slett dokument
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <span className="dok-progress" style={{ width: `${progress}%` }} data-testid="dok-progress" />
        </div>

        <DokIkonCover item={item} patch={patch} docId={doc.id} />

        <textarea
          rows={1}
          defaultValue={item.title}
          key={doc.id}
          onChange={(e) => {
            clearTimeout(tittelT.current);
            const v = e.target.value;
            tittelT.current = setTimeout(() => v.trim() && patch({ title: v }, true), 800);
          }}
          className="dok-tittel-input"
          placeholder="Uten tittel"
          data-testid="dok-tittel-input"
        />

        <div className="dok-byline" data-testid="dok-meta">
          <span className="inline-flex items-center gap-2">
            {bylineBilde
              ? <span className="dok-byline-avatar foto"><img src={bylineBilde} alt="" /></span>
              : <span className="dok-byline-avatar">{(item.updatedBy || "?").slice(0, 1).toUpperCase()}</span>}
            <span className="dok-meta-sterk">{item.updatedBy || "—"}</span>
          </span>
          <span className="dok-skilledot" />
          <span>{new Date(item.updatedAt).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
          <span className="dok-skilledot" />
          <span data-testid="dok-ordteller">{ord} ord · {lesetid} min</span>
          {item.shareActive && (
            <>
              <span className="dok-skilledot" />
              <span className="text-emerald-600">Delt</span>
            </>
          )}
        </div>

        {puls && (puls.relasjoner > 0 || puls.handlinger > 0 || puls.beslutninger > 0) && (
          <div className="dok-pulsrad" data-testid="dok-puls">
            <span><span className="dok-puls-tall">{puls.relasjoner}</span> targets</span>
            <span><span className="dok-puls-tall">{puls.now ?? 0}</span> now</span>
            <span><span className="dok-puls-tall">{puls.dialoger}</span> aktive dialoger</span>
            <span className={puls.uten ? "varsle" : ""}><span className="dok-puls-tall">{puls.uten ?? 0}</span> mangler eier</span>
            {puls.beslutninger > 0 && <span><span className="dok-puls-tall">{puls.beslutninger}</span> åpne beslutninger</span>}
            {puls.handlinger > 0 && <span><span className="dok-puls-tall">{puls.handlinger}</span> handlinger</span>}
          </div>
        )}

        <div className="mt-3.5 mb-5">
          <SakKobling doc={item} linkedTasks={saker} aktor={aktor} onOppdater={(f) => patch(f, true)} />
        </div>

        {iTabell && (
          <div className="dok-tabellbar" data-testid="dok-tabellbar">
            <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()} className="dok-tknapp" data-testid="dok-t-rad"><Rows3 size={11} /> + Rad</button>
            <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()} className="dok-tknapp" data-testid="dok-t-kolonne"><Columns3 size={11} /> + Kolonne</button>
            <button type="button" onClick={() => editor.chain().focus().deleteRow().run()} className="dok-tknapp" data-testid="dok-t-slett-rad">Slett rad</button>
            <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()} className="dok-tknapp" data-testid="dok-t-slett-kolonne">Slett kolonne</button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeaderRow().run()} className="dok-tknapp" data-testid="dok-t-header">Headerrad</button>
            <button type="button" onClick={() => editor.chain().focus().deleteTable().run()} className="dok-tknapp slett" data-testid="dok-t-slett"><Trash size={11} /> Slett tabell</button>
          </div>
        )}

        <input ref={filRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
          onChange={(e) => { lastOppBilde(e.target.files?.[0]); e.target.value = ""; }} data-testid="dok-bilde-input" />

        <div className={`dok-prose mt-6${item.pageFont === "sans" ? " sansfont" : ""}${item.pageSmall ? " liten" : ""}`} data-testid="dok-innhold">
          <EditorContent editor={editor} />
        </div>

        <BubbleMenu editor={editor} options={BOBLE_OPTIONS} shouldShow={bobleShouldShow}>
          <div className="dok-boble" data-testid="dok-boble">
            <div className="relative">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOmgjor((v) => !v)}
                className="dok-bknapp omgjor" title="Gjør om til" data-testid="dok-b-omgjor">
                <OmgjorIco size={12} />
                <span>{omgjorAktiv.label}</span>
                <ChevronDown size={10} className="opacity-60" />
              </button>
              {omgjor && (
                <div className="dok-omgjor-meny" data-testid="dok-omgjor-meny">
                  <p className="dok-omgjor-tittel">Gjør om til</p>
                  {OMGJOR_VALG.map((v) => (
                    <button key={v.id} type="button" onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { v.run(editor); setOmgjor(false); }}
                      className={`dok-omgjor-valg ${v.aktiv(editor) ? "aktiv" : ""}`} data-testid={`dok-omgjor-${v.id}`}>
                      <v.Ico size={12} /> <span className="flex-1 text-left">{v.label}</span>
                      {v.aktiv(editor) && <Check size={11} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="dok-vskille" />
            <BKnapp onClick={() => editor.chain().focus().toggleBold().run()} aktiv={editor.isActive("bold")} title="Fet (⌘B)" testid="dok-b-fet"><Bold size={12} /></BKnapp>
            <BKnapp onClick={() => editor.chain().focus().toggleItalic().run()} aktiv={editor.isActive("italic")} title="Kursiv (⌘I)" testid="dok-b-kursiv"><Italic size={12} /></BKnapp>
            <BKnapp onClick={() => editor.chain().focus().toggleStrike().run()} aktiv={editor.isActive("strike")} title="Gjennomstreket (⌘⇧S)" testid="dok-b-strek"><Strikethrough size={12} /></BKnapp>
            <BKnapp onClick={() => editor.chain().focus().toggleHighlight().run()} aktiv={editor.isActive("highlight")} title="Marker (⌘⇧H)" testid="dok-b-marker"><Highlighter size={12} /></BKnapp>
            <BKnapp onClick={() => apneLenke("meny")} aktiv={editor.isActive("link")} title="Lenke (⌘K)" testid="dok-b-lenke"><Link2 size={12} /></BKnapp>
            <div className="relative">
              <BKnapp onClick={() => setFargeMeny((v) => !v)}
                aktiv={!!editor.getAttributes("textStyle").color || !!editor.getAttributes("textStyle").backgroundColor}
                title="Tekstfarge" testid="dok-b-farge"><Palette size={12} /></BKnapp>
              {fargeMeny && (
                <div className="dok-farge-meny" data-testid="dok-farge-meny">
                  <p className="dok-omgjor-tittel">Tekstfarge</p>
                  <div className="dok-farge-grid">
                    {TEKST_FARGER.map((f) => (
                      <button key={f.id} type="button" onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { if (f.farge) editor.chain().focus().setColor(f.farge).run(); else editor.chain().focus().unsetColor().run(); }}
                        className="dok-farge-swatch" style={f.farge ? { color: f.farge } : undefined}
                        title={f.label} data-testid={`dok-farge-t-${f.id}`}>A</button>
                    ))}
                  </div>
                  <p className="dok-omgjor-tittel">Bakgrunn</p>
                  <div className="dok-farge-grid">
                    {BAKGRUNN_FARGER.map((f) => (
                      <button key={f.id} type="button" onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { if (f.farge) editor.chain().focus().setBackgroundColor(f.farge).run(); else editor.chain().focus().unsetBackgroundColor().run(); }}
                        className={`dok-farge-swatch bg ${!f.farge ? "ingen" : ""}`} style={f.farge ? { background: f.farge } : undefined}
                        title={f.label} data-testid={`dok-farge-b-${f.id}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </BubbleMenu>

        <SlashMeny slash={slash} valg={slashValg} aktivIdx={slashIdx} onKjor={kjorSlash} onHover={setSlashIdx} />
        {mention && (
          <div className="dok-mention-meny" style={{ left: mention.x, top: mention.y }} data-testid="dok-mention-meny">
            {mentionTreff.map((t, i) => (
              <button key={`${t.kilde}-${t.id}`} type="button"
                className={`dok-mention-valg${i === mentionIdx ? " aktiv" : ""}`}
                onMouseEnter={() => setMentionIdx(i)}
                onMouseDown={(e) => { e.preventDefault(); settInnMention(t); }}
                data-testid={`dok-mention-valg-${t.id}`}>
                {t.bilde
                  ? <i className="mavatar"><img src={t.bilde} alt="" /></i>
                  : <em className={`tag ${t.kilde}`}>{t.kilde === "speakers" ? "SPK" : t.kilde === "partners" ? "PRT" : t.kilde === "users" ? "USR" : "SAK"}</em>}
                <span className="truncate">{t.navn}</span>
                <span className="under">{t.stageLabel}</span>
              </button>
            ))}
            {mentionTreff.length === 0 && <p className="dok-mention-tom">Ingen treff — skriv videre eller Esc</p>}
          </div>
        )}

        <DragHandle editor={editor} className="dok-handtak-holder"
          onNodeChange={({ node, pos }) => { dragInfo.current = { node, pos }; }}>
          <div className="dok-handtak" data-testid="dok-handtak">
            <button type="button" className="dok-handtak-knapp" title="Ny blokk under"
              onMouseDown={(e) => e.preventDefault()} onClick={settInnNy} data-testid="dok-handtak-ny">
              <Plus size={13} />
            </button>
            <button type="button" className="dok-handtak-knapp grep" title="Dra for å flytte — klikk for meny"
              onClick={(e) => {
                const { node, pos } = dragInfo.current;
                if (node) setBlokkMeny({ x: e.clientX, y: Math.min(e.clientY, window.innerHeight - 380), node, pos });
              }}
              data-testid="dok-handtak-grep">
              <GripVertical size={13} />
            </button>
          </div>
        </DragHandle>

        {blokkMeny && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setBlokkMeny(null)} />
            <div className="dok-blokkmeny" style={{ left: Math.min(blokkMeny.x, window.innerWidth - 230), top: blokkMeny.y }} data-testid="dok-blokkmeny">
              <button type="button" onClick={dupliserBlokk} className="dok-blokkmeny-valg" data-testid="dok-blokkmeny-dupliser">
                <Copy size={12} /> Dupliser blokk
              </button>
              <button type="button" onClick={slettBlokk} className="dok-blokkmeny-valg slett" data-testid="dok-blokkmeny-slett">
                <Trash2 size={12} /> Slett blokk
              </button>
              {TEKST_TYPER.includes(blokkMeny.node.type.name) && (
                <>
                  <div className="dok-blokkmeny-skille" />
                  <p className="dok-blokkmeny-tittel">Gjør om til</p>
                  {OMGJOR_VALG.map((v) => (
                    <button key={v.id} type="button" onClick={() => omgjorBlokk(v)} className="dok-blokkmeny-valg" data-testid={`dok-blokkmeny-${v.id}`}>
                      <v.Ico size={12} /> {v.label}
                    </button>
                  ))}
                </>
              )}
            </div>
          </>
        )}

        {lenkePop && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setLenkePop(null)} data-testid="dok-lenke-backdrop" />
            <div className="dok-lenkepop" style={{ left: lenkePop.x, top: lenkePop.y }} data-testid="dok-lenkepop">
              <input
                autoFocus
                defaultValue={lenkePop.href}
                placeholder="Lim inn eller skriv lenke …"
                className="dok-lenkepop-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); lagreLenke(e.target.value); }
                  if (e.key === "Escape") setLenkePop(null);
                }}
                data-testid="dok-lenke-input"
              />
              <div className="dok-lenkepop-rad">
                <button type="button" className="dok-lenkepop-knapp primaer" data-testid="dok-lenke-lagre"
                  onClick={(e) => lagreLenke(e.currentTarget.closest(".dok-lenkepop").querySelector("input").value)}>
                  <Check size={11} /> Lagre
                </button>
                {lenkePop.href && (
                  <>
                    <button type="button" className="dok-lenkepop-knapp" onClick={() => window.open(lenkePop.href, "_blank", "noopener")} data-testid="dok-lenke-apne">
                      <ExternalLink size={11} /> Åpne
                    </button>
                    <button type="button" className="dok-lenkepop-knapp" onClick={() => lagreLenke("")} data-testid="dok-lenke-fjern">
                      <Unlink size={11} /> Fjern
                    </button>
                  </>
                )}
                <span className="flex-1" />
                <kbd className="dok-kbd-hint">↵</kbd>
              </div>
            </div>
          </>
        )}
      </div>

      {!tocSkjult && (
        <aside className="hidden xl:block w-[190px] shrink-0 sticky top-6">
          <DokToc overskrifter={overskrifter} editor={editor} />
        </aside>
      )}

      {delModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[16vh] px-4" data-testid="dok-del-modal">
          <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={() => setDelModal(false)} />
          <div className="sak-modal relative w-full max-w-[440px] bg-white rounded-2xl shadow-2xl border border-[#E4E4E1] p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[15px] font-semibold">Del dokumentet</h2>
              <button type="button" onClick={() => setDelModal(false)} className="text-neutral-400 hover:text-neutral-800" data-testid="dok-del-lukk"><X size={16} /></button>
            </div>
            <p className="text-[12px] text-neutral-500 mb-4">Passordbeskyttet lenke — alle med lenken og koden kan lese dokumentet.</p>
            <div className="space-y-2.5">
              <button type="button"
                onClick={() => { navigator.clipboard.writeText(delUrl); toast.success("Lenke kopiert"); }}
                className="w-full text-left text-[12px] border border-[#E4E4E1] rounded-xl px-3 py-2.5 bg-[#F7F7F5] hover:border-neutral-400 transition-colors truncate"
                data-testid="dok-del-lenke">
                {delUrl}
              </button>
              <div className="flex items-center justify-between border border-[#E4E4E1] rounded-xl px-3 py-2.5">
                <span className="text-[11px] text-neutral-400 uppercase tracking-wider">Tilgangskode</span>
                <button type="button" onClick={() => { navigator.clipboard.writeText(item.sharePasscode || ""); toast.success("Kode kopiert"); }}
                  className="bud-mono text-[14px] font-bold tracking-[0.25em] text-neutral-900" data-testid="dok-del-kode">
                  {item.sharePasscode}
                </button>
              </div>
              <div className="flex items-center justify-between border border-[#E4E4E1] rounded-xl px-3 py-2.5">
                <span className="text-[11px] text-neutral-400 uppercase tracking-wider">Data</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => settDataMode("snapshot")}
                    className={`text-[11px] font-medium rounded-lg px-2.5 py-1 border transition-colors ${(item.shareDataMode || "snapshot") === "snapshot" ? "bg-neutral-900 text-white border-neutral-900" : "border-[#E4E4E1] text-neutral-500 hover:border-neutral-400"}`}
                    data-testid="dok-del-mode-snapshot">Snapshot</button>
                  <button type="button" onClick={() => settDataMode("live")}
                    className={`text-[11px] font-medium rounded-lg px-2.5 py-1 border transition-colors ${item.shareDataMode === "live" ? "bg-neutral-900 text-white border-neutral-900" : "border-[#E4E4E1] text-neutral-500 hover:border-neutral-400"}`}
                    data-testid="dok-del-mode-live">Live</button>
                </div>
              </div>
              {(item.shareDataMode || "snapshot") === "snapshot" && item.snapshotCreatedAt && (
                <div className="flex items-center justify-between px-1 text-[11px] text-neutral-400">
                  <span data-testid="dok-del-snapshot-tid">
                    Snapshot fra {new Date(item.snapshotCreatedAt).toLocaleString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <button type="button" onClick={oppdaterSnapshot} className="font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-testid="dok-del-snapshot-oppdater">
                    Oppdater snapshot
                  </button>
                </div>
              )}
              <button type="button"
                onClick={async () => {
                  const { data } = await api.patch(`/admin/docs/${doc.id}/share`, { active: !item.shareActive });
                  setItem(data.item);
                }}
                className="w-full text-[12px] font-medium border border-[#E4E4E1] rounded-xl px-3 py-2.5 text-neutral-600 hover:border-neutral-400 transition-colors"
                data-testid="dok-del-toggle">
                {item.shareActive ? "Deaktiver lenken" : "Aktiver lenken"}
              </button>
            </div>
          </div>
        </div>
      )}

      {versjoner !== null && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="dok-versjoner-drawer">
          <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={() => setVersjoner(null)} />
          <aside className="sak-drawer relative w-full max-w-[380px] h-full bg-white shadow-2xl border-l border-[#E4E4E1] flex flex-col">
            <div className="px-5 pt-4 pb-3 border-b border-neutral-100 flex items-center justify-between">
              <p className="text-[14px] font-semibold">Versjonshistorikk</p>
              <button type="button" onClick={() => setVersjoner(null)} className="text-neutral-400 hover:text-neutral-800" data-testid="dok-versjoner-lukk"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {versjoner.length === 0 && <p className="text-[12px] text-neutral-400">Ingen tidligere versjoner ennå — de lagres automatisk mens du skriver.</p>}
              {versjoner.map((v) => (
                <div key={v.id} className="border border-[#E4E4E1] rounded-xl px-3.5 py-2.5 flex items-center justify-between gap-3" data-testid={`dok-versjon-${v.id}`}>
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-neutral-900">
                      {new Date(v.savedAt).toLocaleString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="text-[11px] text-neutral-400 truncate">av {v.savedBy || "—"}</p>
                  </div>
                  <button type="button" onClick={() => gjenopprett(v.id)}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-neutral-600 border border-[#E4E4E1] rounded-lg px-2 py-1 hover:border-neutral-400 transition-colors"
                    data-testid={`dok-gjenopprett-${v.id}`}>
                    <CircleCheck size={11} /> Gjenopprett
                  </button>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
